import {
  DOCS_V2_BASE_ROUTE,
  DOCUMENTATION_RESOURCE_PATH,
} from '../api/documentationApi';
import {
  getDocumentationRoute,
  normalizeDocumentTagName,
} from './documentationRoutes';

export type ResolvedDocumentationLink =
  | { kind: 'hash'; hash: string }
  | { kind: 'external'; href: string }
  | { kind: 'internal'; path: string }
  | { kind: 'unresolved'; href: string };

export interface ResolveDocumentationLinkOptions {
  href: string;
}

export function resolveDocumentationLink({
  href,
}: ResolveDocumentationLinkOptions): ResolvedDocumentationLink {
  const trimmedHref = href.trim();

  if (!trimmedHref) {
    return { kind: 'unresolved', href };
  }

  if (trimmedHref.startsWith('#')) {
    return { kind: 'hash', hash: trimmedHref.slice(1) };
  }

  const hrefProtocol = getHrefProtocol(trimmedHref);
  if (hrefProtocol === 'mailto' || hrefProtocol === 'tel') {
    return { kind: 'external', href: trimmedHref };
  }

  if (
    hrefProtocol &&
    hrefProtocol !== 'http' &&
    hrefProtocol !== 'https'
  ) {
    return { kind: 'unresolved', href: trimmedHref };
  }

  const portalPath = extractPortalDocumentationPath(trimmedHref);
  if (portalPath) {
    return { kind: 'internal', path: portalPath };
  }

  const resourceBrokerDocumentPath =
    extractResourceBrokerDocumentPath(trimmedHref);
  if (resourceBrokerDocumentPath) {
    return { kind: 'internal', path: resourceBrokerDocumentPath };
  }

  if (isAbsoluteHttpUrl(trimmedHref) || trimmedHref.startsWith('//')) {
    return { kind: 'external', href: trimmedHref };
  }

  const documentTagPath = getDocumentTagPath(trimmedHref);
  if (documentTagPath) {
    return { kind: 'internal', path: documentTagPath };
  }

  return { kind: 'unresolved', href: trimmedHref };
}

function extractPortalDocumentationPath(
  href: string,
): string | undefined {
  if (
    href.startsWith(`${DOCS_V2_BASE_ROUTE}/`) ||
    href === DOCS_V2_BASE_ROUTE
  ) {
    return href;
  }

  try {
    const parsedUrl = new URL(href);
    if (
      parsedUrl.pathname === DOCS_V2_BASE_ROUTE ||
      parsedUrl.pathname.startsWith(`${DOCS_V2_BASE_ROUTE}/`)
    ) {
      return `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
    }
  } catch {
    return undefined;
  }

  return undefined;
}

function extractResourceBrokerDocumentPath(
  href: string,
): string | undefined {
  try {
    const parsedUrl = new URL(href);
    const documentationPathPrefix =
      `${DOCUMENTATION_RESOURCE_PATH}/`;

    if (!parsedUrl.pathname.startsWith(documentationPathPrefix)) {
      return undefined;
    }

    const documentTagName = normalizeDocumentTagName(
      parsedUrl.pathname.slice(documentationPathPrefix.length),
    );

    if (!documentTagName) {
      return undefined;
    }

    return `${getDocumentationRoute(documentTagName)}${parsedUrl.search}${parsedUrl.hash}`;
  } catch {
    return undefined;
  }
}

function getDocumentTagPath(href: string): string | undefined {
  try {
    const parsedUrl = new URL(href, 'https://docs.local/');
    const documentTagName = normalizeDocumentTagName(
      parsedUrl.pathname,
    );

    if (!documentTagName) {
      return undefined;
    }

    return `${getDocumentationRoute(documentTagName)}${parsedUrl.search}${parsedUrl.hash}`;
  } catch {
    return undefined;
  }
}

function getHrefProtocol(href: string): string | undefined {
  const protocolMatch = href.match(/^([a-z][a-z\d+.-]*):/i);
  return protocolMatch?.[1].toLowerCase();
}

function isAbsoluteHttpUrl(href: string): boolean {
  return /^https?:\/\//i.test(href);
}
